import type { SyntheticEvent } from 'react';
import { useMemo } from 'react';
import { getIconFromRegistry } from '@unifyapps/icons/utils/getIconFromRegistry';
import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { clsx } from 'clsx';
import type { FeatureIconProps } from '@unifyapps/ui/components/FeaturedIcon';
import { FeaturedIcon } from '@unifyapps/ui/components/FeaturedIcon';
import { getConditionalPropertyValue } from '@unifyapps/carbon/utils/conditionalValues';
import type { StyleObject } from '@unifyapps/defs/types/styleObject';
import type { TypographyColors } from '@unifyapps/defs/types/typography';
import { iconVariants } from './style';

type Props = {
  icon?: string;
  hasClickEvent?: boolean;
  attributesAndEventListeners?: Record<string, unknown>;
  onClick?: (e: SyntheticEvent) => void;
  variant?: 'transparent' | 'featured';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  styles?: Pick<StyleObject, 'padding' | 'margin' | 'borderRadius'>;
  featured?: {
    color: string;
  };
  transparent?: {
    color: TypographyColors;
  };
  className?: string;
};

export function Icon({
  icon,
  variant,
  size,
  styles,
  featured,
  transparent,
  hasClickEvent,
  attributesAndEventListeners,
  onClick,
  className,
}: Props) {
  const { className: blockAppearanceClassName, style } = useBlockAppearanceStyles({
    appearanceStyles: styles,
  });

  const iconName = getConditionalPropertyValue({ value: icon });
  const IconComponent = getIconFromRegistry(iconName);

  const color = useMemo(
    () =>
      getConditionalPropertyValue({
        value: variant === 'transparent' ? transparent?.color : featured?.color,
      }),
    [featured?.color, transparent?.color, variant],
  );

  if (!IconComponent) {
    return null;
  }

  if (variant === 'transparent') {
    return (
      <IconComponent
        {...attributesAndEventListeners}
        className={clsx(
          iconVariants({ size }),
          blockAppearanceClassName,
          color,
          {
            'cursor-pointer': hasClickEvent,
          },
          className,
        )}
        onClick={onClick}
        style={style}
      />
    );
  }

  return (
    <FeaturedIcon
      {...attributesAndEventListeners}
      Icon={IconComponent}
      className={clsx(blockAppearanceClassName, { 'cursor-pointer': hasClickEvent }, className)}
      color={color as FeatureIconProps['color']}
      onClick={onClick}
      size={size}
    />
  );
}
